import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import useOrders from "../../Services/useOrders";
// import ChaafoLogo from "../../../Images/chaafo-logo.svg";

// Shimmer Loader
const Shimmer = () => (
  <div className="max-w-3xl mx-auto mt-10 p-6 bg-white rounded-2xl shadow-md space-y-4 animate-pulse">
    <div className="h-6 w-56 bg-gray-200 rounded" />
    <div className="h-4 w-3/4 bg-gray-200 rounded" />
    <div className="h-4 w-full bg-gray-200 rounded" />
    <div className="h-4 w-5/6 bg-gray-200 rounded" />
    <div className="h-40 w-full bg-gray-200 rounded" />
  </div>
);

const safeStr = (v, fallback = "") => {
  if (v === null || v === undefined) return fallback;
  const s = String(v).trim();
  return s.length ? s : fallback;
};

const num = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const fmtMoney = (v) => `₹${num(v).toFixed(2)}`;

const parsePercent = (v, fallback = 0) => {
  if (v === null || v === undefined) return fallback;
  const s = String(v).trim();
  const m = s.match(/([0-9]+(\.[0-9]+)?)/);
  return m ? Number(m[1]) : fallback;
};

// Small helper (dummy) — you can replace later with full INR-to-words
const amountInWordsDummy = (amount) => {
  const a = num(amount);
  return a <= 0
    ? "Zero Rupees Only"
    : "Amount in words (dummy): " + a.toFixed(2) + " INR";
};

const getCorsImageUrl = (imageUrl) => {
  if (!imageUrl) return "";

  return `https://images.weserv.nl/?url=${encodeURIComponent(
    imageUrl.replace(/^https?:\/\//, ""),
  )}&output=png`;
};

const OrderSummary = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const orderId = searchParams.get("orderid");
  const shouldAutoDownload = searchParams.get("download") === "1";

  const { getOrderReceiptDetails } = useOrders();

  // Receipt received from Checkout after WebSocket payment success
 const storedReceipt = useMemo(() => {
   if (!orderId) {
     return null;
   }

   try {
     const savedReceipt = localStorage.getItem(`invoice-receipt-${orderId}`);

     return savedReceipt ? JSON.parse(savedReceipt) : null;
   } catch (error) {
     console.error("Stored invoice receipt parse error:", error);

     return null;
   }
 }, [orderId]);

 const receivedReceipt = location.state?.receipt || storedReceipt || null;

  console.log("📄 Receipt received from Checkout:", receivedReceipt);

  // If receipt already came from Checkout, use it directly
  const [order, setOrder] = useState(receivedReceipt);

  // No loader needed if receipt already exists
  const [loading, setLoading] = useState(!receivedReceipt);

  const invoiceRef = useRef(null);
  const downloadStartedRef = useRef(false);

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState("");

  const originalRestaurantLogo = String(order?.organizationLogo || "").trim();

  const completeRestaurantLogo = originalRestaurantLogo.startsWith("http")
    ? originalRestaurantLogo
    : originalRestaurantLogo
      ? `https://anlook.com/${originalRestaurantLogo.replace(/^\/+/, "")}`
      : "";

  const chaafoLogoUrl = useMemo(() => {
    return getCorsImageUrl("https://anlook.com/images/chaafo.png");
  }, []);

  const restaurantLogoUrl = useMemo(() => {
    const originalLogo = String(order?.organizationLogo || "").trim();

    if (!originalLogo) {
      return "";
    }

    const completeLogoUrl = originalLogo.startsWith("http")
      ? originalLogo
      : `https://anlook.com/${originalLogo.replace(/^\/+/, "")}`;

    return getCorsImageUrl(completeLogoUrl);
  }, [order?.organizationLogo]);

  const handleDownloadPdf = useCallback(async () => {
    if (!invoiceRef.current || downloadingPdf) {
      return;
    }

    try {
      setDownloadingPdf(true);
      setPdfError("");

      const invoiceElement = invoiceRef.current;

      const images = Array.from(invoiceElement.querySelectorAll("img"));

      await Promise.all(
        images.map((image) => {
          if (image.complete && image.naturalWidth > 0) {
            return Promise.resolve();
          }

          return new Promise((resolve) => {
            const timeout = window.setTimeout(resolve, 12000);

            const finish = () => {
              window.clearTimeout(timeout);
              resolve();
            };

            image.addEventListener("load", finish, {
              once: true,
            });

            image.addEventListener("error", finish, {
              once: true,
            });
          });
        }),
      );

      const canvas = await html2canvas(invoiceElement, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        logging: false,
        imageTimeout: 15000,
        removeContainer: true,

        width: 794,
        height: 1123,

        windowWidth: 1200,
        windowHeight: 1400,

        scrollX: 0,
        scrollY: 0,

        onclone: (clonedDocument) => {
          const clonedInvoice = clonedDocument.querySelector(
            '[data-invoice="true"]',
          );

          const clonedScaleWrapper = clonedDocument.querySelector(
            '[data-invoice-scale-wrapper="true"]',
          );

          if (clonedScaleWrapper) {
            clonedScaleWrapper.style.transform = "none";
            clonedScaleWrapper.style.transformOrigin = "top left";
            clonedScaleWrapper.style.width = "794px";
            clonedScaleWrapper.style.height = "auto";
          }

          if (clonedInvoice) {
            clonedInvoice.style.transform = "none";
            clonedInvoice.style.width = "794px";
            clonedInvoice.style.minHeight = "1123px";
            clonedInvoice.style.boxSizing = "border-box";
            clonedInvoice.style.boxShadow = "none";
            clonedInvoice.style.margin = "0";
          }
        },
      });

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imageData = canvas.toDataURL("image/jpeg", 0.98);

      pdf.addImage(
        imageData,
        "JPEG",
        0,
        0,
        pageWidth,
        pageHeight,
        undefined,
        "FAST",
      );

      const safeOrderId = String(order?.orderId || orderId || "invoice")
        .replace(/[^a-zA-Z0-9-_]/g, "-")
        .slice(0, 80);

      pdf.save(`Invoice-${safeOrderId}.pdf`);

      pdf.addImage(
        imageData,
        "JPEG",
        0,
        0,
        pageWidth,
        pageHeight,
        undefined,
        "FAST",
      );

      pdf.save(`Invoice-${safeOrderId}.pdf`);
    } catch (error) {
      console.error("PDF Download Error:", error);

      setPdfError(error?.message || "Invoice PDF download nahi ho paayi.");
    } finally {
      setDownloadingPdf(false);
    }
  }, [downloadingPdf, order, orderId]);

useEffect(() => {
  /*
   * CASE 1:
   * Receipt API Checkout screen me WebSocket payment success
   * ke baad already call ho chuki hai.
   *
   * Therefore duplicate API call mat karo.
   */
  if (receivedReceipt) {
    console.log("✅ Using receipt received from Checkout");

    setOrder(receivedReceipt);
    setLoading(false);

    return;
  }

  /*
   * CASE 2:
   * User directly URL open kare / page refresh kare.
   *
   * React Router state refresh ke baad disappear ho sakti hai,
   * therefore receipt API fallback required hai.
   */
  if (!orderId) {
    setLoading(false);
    return;
  }

  let isMounted = true;

  const fetchReceipt = async () => {
    try {
      setLoading(true);

      console.log(
        "🔄 No receipt passed from Checkout. Fetching receipt:",
        orderId,
      );

      const data = await getOrderReceiptDetails({
        orderid: orderId,
        platform: "WEB",
      });

      console.log("📄 Receipt API Response:", data);

      const receiptResponse =
        data?.response ?? data?.data?.response ?? data?.data ?? null;

      const finalReceipt = Array.isArray(receiptResponse)
        ? receiptResponse[0] || null
        : receiptResponse;

      console.log("📄 Final Receipt:", finalReceipt);

      if (isMounted) {
        setOrder(finalReceipt);
      }
    } catch (error) {
      console.error("❌ Receipt API Error:", error);

      if (isMounted) {
        setOrder(null);
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
  };

  fetchReceipt();

  return () => {
    isMounted = false;
  };
}, [getOrderReceiptDetails, orderId, receivedReceipt]);

useEffect(() => {
  // Normal page open hai, auto download nahi karna
  if (!shouldAutoDownload) {
    return;
  }

  // API/loading complete hone do
  if (loading) {
    return;
  }

  // Receipt available honi chahiye
  if (!order) {
    return;
  }

  // Ek hi baar PDF download
  if (downloadStartedRef.current) {
    return;
  }

  const timer = window.setTimeout(() => {
    // Invoice DOM render hone ka wait
    if (!invoiceRef.current) {
      console.error("Invoice element not ready for PDF download.");

      return;
    }

    downloadStartedRef.current = true;

    handleDownloadPdf();
  }, 500);

  return () => {
    window.clearTimeout(timer);
  };
}, [shouldAutoDownload, loading, order, handleDownloadPdf]);

useEffect(() => {
  if (!orderId || !storedReceipt) {
    return;
  }

  const timer = window.setTimeout(() => {
    localStorage.removeItem(`invoice-receipt-${orderId}`);
  }, 5000);

  return () => {
    window.clearTimeout(timer);
  };
}, [orderId, storedReceipt]);

  const derived = useMemo(() => {
    const o = order || {};

    // Dummy fallback placeholders (you will update later)
    const legalEntityName = safeStr(
      o.legalEntityName,
      safeStr(o.organizationName, "Restaurant"),
    );
    const restaurantName = safeStr(
      o.restaurantName,
      safeStr(o.organizationName, "Restaurant"),
    );
    const restaurantAddress = safeStr(o.organizationAddress, "—");
    const gstin = safeStr(o.gst, "—");
    const fssai = safeStr(o.fssai, "—"); // not in API right now

    const invoiceNo = safeStr(
      o.invoiceNo,
      `INV-${safeStr(o.orderId, "00000")}`,
    );
    const invoiceDate = safeStr(o.invoiceDate, safeStr(o.orderDate, "—"));

    const customerName = safeStr(o.customerName, "Customer");
    const customerMobile = safeStr(o.custMobile, "—");
    const deliveryAddress = safeStr(o.deliveryAddress, "—");

    const hsnCode = safeStr(o.hsnCode, "996331"); // dummy
    const serviceDesc = safeStr(o.serviceDescription, "Restaurant Service");

    const items = Array.isArray(o.items) ? o.items : [];

    // Per-item invoice row values
    const itemRows = items.map((it) => {
      const qty = Math.max(1, num(it.quantity, 1));
      const gross = num(it.itemPrice) * qty;

      // item-level discount not in API yet
      const discount = num(it.discountAmount, 0);
      const net = Math.max(0, gross - discount);

      const cgstRate = parsePercent(it.taxCgstRate, 2.5);
      const sgstRate = parsePercent(it.taxSgstRate, 2.5);

      // If API sends item tax amounts later, use them; else compute
      const cgst = num(it.taxCgst, 0) || (net * cgstRate) / 100;
      const sgst = num(it.taxSgst, 0) || (net * sgstRate) / 100;

      const total = net + cgst + sgst;

      return {
        name: safeStr(it.itemName, "Item"),
        qty,
        gross,
        discount,
        net,
        cgstRate,
        cgst,
        sgstRate,
        sgst,
        total,
      };
    });

    const itemsGross = itemRows.reduce((a, r) => a + r.gross, 0);
    const itemsDiscount = itemRows.reduce((a, r) => a + r.discount, 0);
    const itemsNet = itemRows.reduce((a, r) => a + r.net, 0);
    const itemsCgst = itemRows.reduce((a, r) => a + r.cgst, 0);
    const itemsSgst = itemRows.reduce((a, r) => a + r.sgst, 0);
    const itemsTotal = itemRows.reduce((a, r) => a + r.total, 0);

    // Extra charges
    const packagingNet = num(o.packaging_charges, 0);
    const deliveryNet = num(o.deliveryFee, num(o.deliveryCharges, 0));

    // Use API tax totals if present, else estimate from items
    const taxCgst = num(o.tax_cgst, itemsCgst);
    const taxSgst = num(o.tax_sgst, itemsSgst);

    const subtotal = num(o.subtotal, itemsNet);
    const discountAmount = num(o.discountAmount, 0);

    const totalValue = subtotal + packagingNet + deliveryNet;
    const grandTotal = num(
      o.pay_amount,
      totalValue + taxCgst + taxSgst - discountAmount,
    );

    return {
      legalEntityName,
      restaurantName,
      restaurantAddress,
      gstin,
      fssai,
      invoiceNo,
      invoiceDate,
      customerName,
      customerMobile,
      deliveryAddress,
      hsnCode,
      serviceDesc,
      itemRows,
      itemsGross,
      itemsDiscount,
      itemsNet,
      itemsCgst,
      itemsSgst,
      itemsTotal,
      packagingNet,
      deliveryNet,
      subtotal,
      discountAmount,
      taxCgst,
      taxSgst,
      grandTotal,
      watermark: safeStr(o.watermark, ""),
    };
  }, [order]);

  if (loading) return <Shimmer />;
  if (!orderId) {
    return (
      <p className="text-center text-gray-500 mt-8">
        Missing <b>orderid</b> in URL.
      </p>
    );
  }
  if (!order)
    return <p className="text-center text-gray-500 mt-8">Order not found</p>;

  const paymentStatus = safeStr(order.paymentStatus, "unpaid").toLowerCase();

  const td = {
    border: "0.5px solid #000000",
    padding: "6px",
    fontWeight: 400,
    color: "#111111",
  };

  const tdRight = {
    border: "0.5px solid #000000",
    padding: "6px",
    textAlign: "right",
    fontWeight: 400,
    color: "#111111",
  };

  const numberToWords = (amount) => {
    const a = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const b = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    const numToWords = (num) => {
      if (num < 20) return a[num];
      if (num < 100) return b[Math.floor(num / 10)] + " " + a[num % 10];
      if (num < 1000)
        return a[Math.floor(num / 100)] + " Hundred " + numToWords(num % 100);
      if (num < 100000)
        return (
          numToWords(Math.floor(num / 1000)) +
          " Thousand " +
          numToWords(num % 1000)
        );
      if (num < 10000000)
        return (
          numToWords(Math.floor(num / 100000)) +
          " Lakh " +
          numToWords(num % 100000)
        );
      return "";
    };

    const rupees = Math.floor(amount);
    const paisa = Math.round((amount - rupees) * 100);

    return `${numToWords(rupees)} Rupees${
      paisa > 0 ? " And " + numToWords(paisa) + " Paisa" : ""
    } Only`;
  };

  const items = Array.isArray(order.items) ? order.items : [];

  const itemsTotals = items.reduce(
    (acc, it) => {
      const qty = Number(it.quantity || 0);
      acc.qty += qty;

      acc.gross += Number(it.totalPrice || 0); // gross incl. item total
      acc.net += Number(it.netValue || 0); // net (without tax)
      acc.cgst += Number(it.taxCgst || 0);
      acc.sgst += Number(it.taxSgst || 0);
      acc.total += Number(it.totalPrice || 0); // same as gross in your API

      return acc;
    },
    { qty: 0, gross: 0, net: 0, cgst: 0, sgst: 0, total: 0 },
  );
  const grandTotal = parseFloat(order.pay_amount || 0);

  return (
    <div
      style={{
        background: "#e5e5e5",
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        padding: "20px",
      }}
    >
      <div
        data-invoice-scale-wrapper="true"
        style={{
          transform: `scale(${Math.min(window.innerWidth / 850, 1)})`,
          transformOrigin: "top center",
          width: "794px",
        }}
      >
        {/* A4 PAPER */}
        <div
          ref={invoiceRef}
          data-invoice="true"
          style={{
            width: "794px",
            minHeight: "1123px",
            boxSizing: "border-box",
            background: "#ffffff",
            padding: "40px",
            boxShadow: "0 0 20px rgba(0,0,0,0.2)",
            fontFamily: "Arial, sans-serif",
            fontSize: "14px",
            fontWeight: 400,
            color: "#111111",
            position: "relative",
          }}
        >
          {/* HEADER */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "30px",
              width: "100%",
            }}
          >
            {/* LEFT RESTAURANT DETAILS */}
            <div
              style={{
                width: "62%",
                minWidth: 0,
                paddingRight: "10px",
              }}
            >
              {restaurantLogoUrl && (
                <img
                  crossOrigin="anonymous"
                  src={restaurantLogoUrl}
                  alt={order?.organizationName || "Restaurant"}
                  loading="eager"
                  decoding="sync"
                  style={{
                    display: "block",
                    width: "180px",
                    height: "60px",
                    objectFit: "contain",
                    objectPosition: "left center",
                    marginBottom: "12px",
                    backgroundColor: "#ffffff",
                  }}
                />
              )}

              <div
                style={{
                  marginBottom: "5px",
                  lineHeight: 1.4,
                }}
              >
                <b>Restaurant Name:</b>{" "}
                <span>{order.organizationName || "—"}</span>
              </div>

              {/* SEPARATE ADDRESS DIV */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  width: "100%",
                  marginBottom: "5px",
                  lineHeight: 1.45,
                }}
              >
                <b
                  style={{
                    flexShrink: 0,
                    marginRight: "5px",
                  }}
                >
                  Address:
                </b>

                <div
                  style={{
                    width: "340px",
                    maxWidth: "100%",
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    fontWeight: 400,
                  }}
                >
                  {order.organizationAddress || "—"}
                </div>
              </div>

              <div
                style={{
                  marginBottom: "5px",
                  lineHeight: 1.4,
                }}
              >
                <b>Restaurant GSTIN:</b> {order.gst || "—"}
              </div>

              <div
                style={{
                  marginBottom: "5px",
                  lineHeight: 1.4,
                }}
              >
                <b>Restaurant Mobile:</b> {order.organizationMobile || "—"}
              </div>

              <div
                style={{
                  lineHeight: 1.4,
                }}
              >
                <b>Gst Type:</b>{" "}
                {order.gst_type === "excl" ? "Exclusive Gst" : "Inclusive Gst"}
              </div>
            </div>

            {/* RIGHT INVOICE DETAILS */}
            <div
              style={{
                width: "30%",
                flexShrink: 0,
                textAlign: "right",
                paddingLeft: "12px",
              }}
            >
              <img
                crossOrigin="anonymous"
                src={chaafoLogoUrl}
                alt="Chaafo"
                loading="eager"
                decoding="sync"
                style={{
                  display: "block",
                  width: "150px",
                  height: "60px",
                  objectFit: "contain",
                  objectPosition: "right center",
                  marginLeft: "auto",
                  backgroundColor: "#ffffff",
                }}
                onError={(event) => {
                  console.error("Chaafo logo failed:", event.currentTarget.src);
                }}
              />

              <div
                style={{
                  fontWeight: 600,
                  marginTop: "10px",
                  lineHeight: 1.4,
                }}
              >
                TAX INVOICE
              </div>

              <div
                style={{
                  marginTop: "3px",
                  fontSize: "12px",
                  lineHeight: 1.4,
                }}
              >
                ORIGINAL FOR RECIPIENT
              </div>
            </div>
          </div>

          <hr style={{ margin: "20px 0" }} />

          {/* CUSTOMER DETAILS */}
          <div style={{ marginBottom: "20px" }}>
            <div>
              <b>Invoice No:</b> {order.orderId}
            </div>
            <div>
              <b>Invoice Date:</b>{" "}
              {new Date(order.orderDate).toLocaleDateString("en-GB")}
            </div>
            <div>
              <b>Customer Mobile:</b> {order.custMobile}
            </div>
            <div>
              <b>Payment Status:</b>{" "}
              <span
                style={{
                  color: order.paymentStatus === "paid" ? "green" : "red",
                  fontWeight: "bold",
                }}
              >
                {order.paymentStatus.toUpperCase()}
              </span>
            </div>
            <div>
              <b>Delivery Address:</b> {order.deliveryAddress}
            </div>
          </div>

          {/* ITEMS TABLE */}
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              marginBottom: "20px",
              fontFamily: "Arial, sans-serif",
              fontSize: "12px",
              fontWeight: 600,
              color: "#111111",
            }}
          >
            <thead>
              <tr>
                {[
                  "Particulars",
                  "Qty",
                  "Gross",
                  "Net",
                  "CGST",
                  "SGST",
                  "Total",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      border: "0.5px solid #000000",
                      padding: "8px 5px",
                      textAlign: "center",
                      fontWeight: 500,
                      fontSize: "11px",
                      color: "#111111",
                      backgroundColor: "#ffffff",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {/* ==== FOOD ITEMS ==== */}
              {items.map((it, idx) => {
                const qty = Number(it.quantity || 0);
                const gross = Number(it.totalPrice || 0); // item total incl tax
                const net = Number(it.netValue || 0); // base value
                const cgst = Number(it.taxCgst || 0);
                const sgst = Number(it.taxSgst || 0);

                return (
                  <tr key={`${it.itemId}-${idx}`}>
                    <td style={td}>
                      <div
                        style={{
                          fontWeight: 600,
                          color: "#111111",
                        }}
                      >
                        {it.itemName}
                      </div>
                      <div
                        style={{
                          fontSize: "10px",
                          marginTop: "3px",
                          fontWeight: 600,
                          color: "#555555",
                        }}
                      >
                        Net: ₹{net.toFixed(2)} • CGST: ₹{cgst.toFixed(2)} •
                        SGST: ₹{sgst.toFixed(2)}
                      </div>
                    </td>

                    <td style={{ ...td, textAlign: "center" }}>{qty}</td>
                    <td style={tdRight}>₹{gross.toFixed(2)}</td>
                    <td style={tdRight}>
                      ₹
                      {order.gst_type !== "excl"
                        ? net.toFixed(2)
                        : gross.toFixed(2)}
                    </td>
                    <td style={tdRight}>₹{cgst.toFixed(2)}</td>
                    <td style={tdRight}>₹{sgst.toFixed(2)}</td>
                    <td style={{ ...tdRight, fontWeight: "500" }}>
                      ₹
                      {order.gst_type !== "excl"
                        ? gross.toFixed(2)
                        : (gross + cgst + sgst).toFixed(2)}
                    </td>
                  </tr>
                );
              })}

              {/* ==== ITEMS TOTAL ROW ==== */}
              <tr>
                <td style={{ ...td, fontWeight: "500" }}>Item(s) Total</td>
                <td style={{ ...td, textAlign: "center", fontWeight: "500" }}>
                  {itemsTotals.qty}
                </td>
                <td style={{ ...tdRight, fontWeight: "500" }}>
                  ₹{itemsTotals.gross.toFixed(2)}
                </td>
                <td style={{ ...tdRight, fontWeight: "500" }}>
                  ₹
                  {order.gst_type !== "excl"
                    ? itemsTotals.net.toFixed(2)
                    : itemsTotals.gross.toFixed(2)}
                </td>
                <td style={{ ...tdRight, fontWeight: "500" }}>
                  ₹{itemsTotals.cgst.toFixed(2)}
                </td>
                <td style={{ ...tdRight, fontWeight: "500" }}>
                  ₹{itemsTotals.sgst.toFixed(2)}
                </td>
                <td style={{ ...tdRight, fontWeight: "500" }}>
                  ₹
                  {order.gst_type !== "excl"
                    ? itemsTotals.total.toFixed(2)
                    : (
                        itemsTotals.gross +
                        itemsTotals.cgst +
                        itemsTotals.sgst
                      ).toFixed(2)}
                </td>
              </tr>

              {
                <tr>
                  <td
                    colSpan={1}
                    style={{ ...td, fontWeight: "bold", textAlign: "left" }}
                  >
                    Grand Total
                  </td>

                  <td colSpan={6} style={{ ...tdRight, fontWeight: "bold" }}>
                    ₹{grandTotal.toFixed(2)}
                  </td>
                </tr>
              }
            </tbody>
          </table>

          {/* AMOUNT SECTION */}
          <div style={{ marginTop: "30px" }}>
            <div style={{ marginBottom: "8px" }}>
              <b>Amount (in words):</b> {numberToWords(grandTotal)}
            </div>

            <div style={{ marginBottom: "8px" }}>
              Amount of INR {grandTotal.toFixed(2)} settled through digital
              mode/payment received against Order ID: {order.orderId} dated{" "}
              {new Date(order.orderDate).toLocaleDateString("en-GB")}.
            </div>

            <div>Supply attracts reverse charge: No</div>
          </div>

          {/* FOOTER */}
          <div
            style={{
              marginTop: "80px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
            }}
          >
            <div style={{ fontSize: "13px" }}>
              <div>
                <b>For CHAAFO PRIVATE LIMITED</b>
              </div>
              <div>
                <b>CIN: U55101UP2022PTC163671</b>
              </div>
              <div>
                <b>GST: 09AAKCC2923R1Z1</b>
              </div>
              <div>
                {" "}
                <b>FSSAI: 22726922000231</b>
              </div>
              <div>{order.watermark}</div>
            </div>

            <div style={{ textAlign: "right" }}>
              <div style={{ marginBottom: "50px" }}></div>
              <div>
                <b>Authorised Signatory</b>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
